import React, { useState, useEffect, useCallback } from 'react';
import styles from './institutionManager.module.scss';

// Helper function to get basic headers
const getBasicHeaders = () => {
  return {
    'Content-Type': 'application/json',
  };
};

// API Configuration - Auto-detect environment and set appropriate API URL
const getApiBaseUrl = () => {
  // Check if we're in development (localhost) or production
  const isLocalhost = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' ||
                     window.location.hostname.includes('localhost');
  
  // Check if we're on the production domain
  const isProduction = window.location.hostname.includes('cloudlab.com.np') ||
                      window.location.hostname.includes('muaccount');
  
  // Priority: localStorage > auto-detection > default
  const savedUrl = localStorage.getItem('apiBaseUrl');
  
  if (savedUrl) {
    return savedUrl;
  }
  
  if (isLocalhost) {
    return 'http://localhost:8000';
  }
  
  if (isProduction) {
    return 'https://muaccount.api.cloudlab.com.np';
  }
  
  // Default fallback
  return 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

const InstitutionManager = () => {
  const [institutions, setInstitutions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editShortName, setEditShortName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [editCategory, setEditCategory] = useState(5);
  const [newInstitution, setNewInstitution] = useState('');
  const [newInstitutionShortName, setNewInstitutionShortName] = useState('');
  const [newInstitutionCode, setNewInstitutionCode] = useState('');
  const [newInstitutionCategory, setNewInstitutionCategory] = useState(5);
  const [bulkCategory, setBulkCategory] = useState(5);
  const [selectedInstitutions, setSelectedInstitutions] = useState([]);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryCode, setNewCategoryCode] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryCode, setEditCategoryCode] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Fetch all institutions
  const fetchInstitutions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/institutions/`, {
        headers: getBasicHeaders()
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          setInstitutions(result.data);
        } else {
          setMessage('Error fetching institutions: ' + (result.message || 'Unknown error'));
        }
      } else {
        setMessage('Error fetching institutions');
      }
    } catch (error) {
      setMessage('Error fetching institutions: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch all categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/institution-categories/`, {
        headers: getBasicHeaders()
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          setCategories(result.data);
        } else {
          setMessage('Error fetching categories: ' + (result.message || 'Unknown error'));
        }
      } else {
        setMessage('Error fetching categories');
      }
    } catch (error) {
      setMessage('Error fetching categories: ' + error.message);
    }
  }, []);

  useEffect(() => {
    fetchInstitutions();
    fetchCategories();
  }, [fetchInstitutions, fetchCategories]);

  // Add single institution
  const handleAddInstitution = async () => {
    if (!newInstitution.trim()) {
      setMessage('Please enter an institution name');
      return;
    }

    if (!newInstitutionShortName.trim()) {
      setMessage('Please enter a short name');
      return;
    }

    if (!newInstitutionCode.trim()) {
      setMessage('Please enter a code');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/institutions/create/`, {
        method: 'POST',
        headers: getBasicHeaders(),
        body: JSON.stringify({ 
          name: newInstitution.trim(),
          short_name: newInstitutionShortName.trim(),
          code: newInstitutionCode.trim().toUpperCase(),
          category: newInstitutionCategory
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          setInstitutions(prev => [...prev, result.data]);
          setNewInstitution('');
          setNewInstitutionShortName('');
          setNewInstitutionCode('');
          setNewInstitutionCategory(5);
          setMessage('Institution added successfully');
          setTimeout(() => setMessage(''), 3000);
        } else {
          setMessage('Error adding institution: ' + (result.message || 'Unknown error'));
        }
      } else {
        const errorData = await response.json();
        setMessage('Error adding institution: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      setMessage('Error adding institution: ' + error.message);
    }
  };

  // Bulk upload institutions
  const handleBulkUpload = async () => {
    if (!bulkText.trim()) {
      setMessage('Please enter institution names');
      return;
    }

    const institutionNames = bulkText.split('\n').filter(name => name.trim());
    if (institutionNames.length === 0) {
      setMessage('Please enter at least one institution name');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/institutions/bulk-create/`, {
        method: 'POST',
        headers: getBasicHeaders(),
        body: JSON.stringify({ 
          institutions: institutionNames.map(name => ({
            name: name.trim(),
            short_name: name.trim().substring(0, 10),
            code: name.trim().substring(0, 5).toUpperCase(),
            category: bulkCategory
          }))
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          setInstitutions(prev => [...prev, ...result.data]);
          setMessage(`${result.data.length} institutions added successfully`);
          setBulkText('');
          setBulkCategory(5);
          setShowBulkUpload(false);
          setTimeout(() => setMessage(''), 5000);
        } else {
          setMessage('Error uploading institutions: ' + (result.message || 'Unknown error'));
        }
      } else {
        const errorData = await response.json();
        setMessage('Error uploading institutions: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      setMessage('Error uploading institutions: ' + error.message);
    }
  };

  // Add category
  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      setMessage('Please enter a category name');
      return;
    }

    if (!newCategoryCode.trim()) {
      setMessage('Please enter a category code');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/institution-categories/create/`, {
        method: 'POST',
        headers: getBasicHeaders(),
        body: JSON.stringify({ 
          name: newCategory.trim(),
          code: newCategoryCode.trim().toUpperCase(),
          description: ''
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          setCategories(prev => [...prev, result.data]);
          setNewCategory('');
          setNewCategoryCode('');
          setMessage('Category added successfully');
          setTimeout(() => setMessage(''), 3000);
        } else {
          setMessage('Error adding category: ' + (result.message || 'Unknown error'));
        }
      } else {
        const errorData = await response.json();
        setMessage('Error adding category: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      setMessage('Error adding category: ' + error.message);
    }
  };

  // Start editing category
  const handleStartEditCategory = (category) => {
    setEditingCategoryId(category.id);
    setEditCategoryName(category.name);
    setEditCategoryCode(category.code);
  };

  // Save category edit
  const handleSaveCategoryEdit = async () => {
    if (!editCategoryName.trim()) {
      setMessage('Please enter a category name');
      return;
    }

    if (!editCategoryCode.trim()) {
      setMessage('Please enter a category code');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/institution-categories/${editingCategoryId}/update/`, {
        method: 'PUT',
        headers: getBasicHeaders(),
        body: JSON.stringify({ 
          name: editCategoryName.trim(),
          code: editCategoryCode.trim().toUpperCase(),
          description: ''
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          setCategories(prev => 
            prev.map(cat => cat.id === editingCategoryId ? result.data : cat)
          );
          setEditingCategoryId(null);
          setEditCategoryName('');
          setEditCategoryCode('');
          setMessage('Category updated successfully');
          setTimeout(() => setMessage(''), 3000);
        } else {
          setMessage('Error updating category: ' + (result.message || 'Unknown error'));
        }
      } else {
        const errorData = await response.json();
        setMessage('Error updating category: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      setMessage('Error updating category: ' + error.message);
    }
  };

  // Cancel category edit
  const handleCancelCategoryEdit = () => {
    setEditingCategoryId(null);
    setEditCategoryName('');
    setEditCategoryCode('');
  };

  // Delete category
  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/institution-categories/${id}/delete/`, {
        method: 'DELETE',
        headers: getBasicHeaders(),
      });

      if (response.ok) {
        setCategories(prev => prev.filter(cat => cat.id !== id));
        setMessage('Category deleted successfully');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setMessage('Error deleting category: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      setMessage('Error deleting category: ' + error.message);
    }
  };

  // Start editing
  const handleStartEdit = (institution) => {
    setEditingId(institution.id);
    setEditName(institution.name);
    setEditShortName(institution.short_name || '');
    setEditCode(institution.code || '');
    setEditCategory(institution.category || 5);
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editName.trim()) {
      setMessage('Please enter an institution name');
      return;
    }

    if (!editShortName.trim()) {
      setMessage('Please enter a short name');
      return;
    }

    if (!editCode.trim()) {
      setMessage('Please enter a code');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/institutions/${editingId}/update/`, {
        method: 'PUT',
        headers: getBasicHeaders(),
        body: JSON.stringify({ 
          name: editName.trim(),
          short_name: editShortName.trim(),
          code: editCode.trim().toUpperCase(),
          category: editCategory
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          setInstitutions(prev => 
            prev.map(inst => inst.id === editingId ? result.data : inst)
          );
          setEditingId(null);
          setEditName('');
          setEditShortName('');
          setEditCode('');
          setEditCategory(5);
          setMessage('Institution updated successfully');
          setTimeout(() => setMessage(''), 3000);
        } else {
          setMessage('Error updating institution: ' + (result.message || 'Unknown error'));
        }
      } else {
        const errorData = await response.json();
        setMessage('Error updating institution: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      setMessage('Error updating institution: ' + error.message);
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditShortName('');
    setEditCode('');
    setEditCategory(5);
  };

  // Delete institution
  const handleDeleteInstitution = async (id) => {
    if (!window.confirm('Are you sure you want to delete this institution?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/institutions/${id}/delete/`, {
        method: 'DELETE',
        headers: getBasicHeaders(),
      });

      if (response.ok) {
        setInstitutions(prev => prev.filter(inst => inst.id !== id));
        setMessage('Institution deleted successfully');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setMessage('Error deleting institution: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      setMessage('Error deleting institution: ' + error.message);
    }
  };

  // Handle bulk selection
  const handleSelectInstitution = (id) => {
    setSelectedInstitutions(prev => 
      prev.includes(id) 
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedInstitutions.length === 0) {
      setMessage('Please select institutions to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedInstitutions.length} institutions?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/institutions/bulk-delete/`, {
        method: 'POST',
        headers: getBasicHeaders(),
        body: JSON.stringify({ institution_ids: selectedInstitutions }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          setInstitutions(prev => prev.filter(inst => !selectedInstitutions.includes(inst.id)));
          setSelectedInstitutions([]);
          setMessage(`${result.deleted_count} institutions deleted successfully`);
          setTimeout(() => setMessage(''), 3000);
        } else {
          setMessage('Error deleting institutions: ' + (result.message || 'Unknown error'));
        }
      } else {
        const errorData = await response.json();
        setMessage('Error deleting institutions: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      setMessage('Error deleting institutions: ' + error.message);
    }
  };

  // Pagination and filtering logic
  const filteredInstitutions = institutions.filter(institution => {
    const searchLower = searchTerm.toLowerCase();
    return (
      institution.name.toLowerCase().includes(searchLower) ||
      (institution.short_name && institution.short_name.toLowerCase().includes(searchLower)) ||
      (institution.code && institution.code.toLowerCase().includes(searchLower)) ||
      (institution.category_name && institution.category_name.toLowerCase().includes(searchLower))
    );
  });

  const sortedInstitutions = [...filteredInstitutions].sort((a, b) => {
    let aValue = a[sortField] || '';
    let bValue = b[sortField] || '';
    
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const totalPages = Math.ceil(sortedInstitutions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentInstitutions = sortedInstitutions.slice(startIndex, endIndex);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  return (
    <div className={styles.container}>

      {/* Header */}
      <div className={styles.header}>
        <h1>मध्यपश्चिम विश्वबिधालय</h1>
        <h2>Institution Management</h2>
      </div>

      {/* Message */}
      {message && (
        <div className={styles.message}>
          {message}
        </div>
      )}

      {/* Actions */}
      <div className={styles.actions}>
        <div className={styles.actionButtons}>
          <button 
            onClick={() => setShowCategoryManager(!showCategoryManager)} 
            className={styles.button}
          >
            {showCategoryManager ? 'Hide Category Manager' : 'Manage Categories'}
          </button>
          
          <button 
            onClick={() => setShowBulkUpload(!showBulkUpload)} 
            className={styles.button}
          >
            {showBulkUpload ? 'Hide Bulk Upload' : 'Bulk Upload'}
          </button>
          
          {selectedInstitutions.length > 0 && (
            <button onClick={handleBulkDelete} className={styles.deleteButton}>
              Delete Selected ({selectedInstitutions.length})
            </button>
          )}
        </div>
      </div>

      {/* Category Manager */}
      {showCategoryManager && (
        <div className={styles.categoryManager}>
          <h3>Category Management</h3>
          
          {/* Add Category Form */}
          <div className={styles.addCategoryForm}>
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Category name"
              className={styles.input}
            />
            <input
              type="text"
              value={newCategoryCode}
              onChange={(e) => setNewCategoryCode(e.target.value)}
              placeholder="Category code"
              className={styles.input}
            />
            <button onClick={handleAddCategory} className={styles.button}>
              Add Category
            </button>
          </div>

          {/* Categories List */}
          <div className={styles.categoriesList}>
            <h4>Categories ({categories.length})</h4>
            <div className={styles.categoriesTable}>
              {categories.map((category) => (
                <div key={category.id} className={styles.categoryItem}>
                  {editingCategoryId === category.id ? (
                    <>
                      <input
                        type="text"
                        value={editCategoryName}
                        onChange={(e) => setEditCategoryName(e.target.value)}
                        className={styles.editInput}
                      />
                      <input
                        type="text"
                        value={editCategoryCode}
                        onChange={(e) => setEditCategoryCode(e.target.value)}
                        className={styles.editInput}
                      />
                      <button onClick={handleSaveCategoryEdit} className={styles.saveButton}>
                        Save
                      </button>
                      <button onClick={handleCancelCategoryEdit} className={styles.cancelButton}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <span>{category.name}</span>
                      <span>{category.code}</span>
                      <button 
                        onClick={() => handleStartEditCategory(category)} 
                        className={styles.editButton}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteCategory(category.id)} 
                        className={styles.deleteButton}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add Institution and Bulk Upload Section */}
      <div className={styles.addSection}>
        {/* Add Institution */}
        <div className={styles.addInstitution}>
          <h3>Add Institution</h3>
          <div className={styles.addForm}>
            <input
              type="text"
              value={newInstitution}
              onChange={(e) => setNewInstitution(e.target.value)}
              placeholder="Institution name"
              className={styles.input}
              onKeyPress={(e) => e.key === 'Enter' && handleAddInstitution()}
            />
            <input
              type="text"
              value={newInstitutionShortName}
              onChange={(e) => setNewInstitutionShortName(e.target.value)}
              placeholder="Short name"
              className={styles.input}
              onKeyPress={(e) => e.key === 'Enter' && handleAddInstitution()}
            />
            <input
              type="text"
              value={newInstitutionCode}
              onChange={(e) => setNewInstitutionCode(e.target.value)}
              placeholder="Code"
              className={styles.input}
              onKeyPress={(e) => e.key === 'Enter' && handleAddInstitution()}
            />
            <select
              value={newInstitutionCategory}
              onChange={(e) => setNewInstitutionCategory(parseInt(e.target.value))}
              className={styles.select}
            >
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <button onClick={handleAddInstitution} className={styles.button}>
              Add Institution
            </button>
          </div>
        </div>

        {/* Bulk Upload */}
        {showBulkUpload && (
          <div className={styles.bulkUpload}>
            <h3>Bulk Upload</h3>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder="Enter institution names (one per line)"
              className={styles.textarea}
              rows={8}
            />
            <select
              value={bulkCategory}
              onChange={(e) => setBulkCategory(parseInt(e.target.value))}
              className={styles.select}
            >
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <button onClick={handleBulkUpload} className={styles.button}>
              Upload Institutions
            </button>
          </div>
        )}
      </div>

      {/* Institutions List */}
      <div className={styles.institutionsList}>
        <h3>Institutions ({institutions.length})</h3>
        
        {/* Search and Filter Section */}
        <div className={styles.searchSection}>
          <div className={styles.searchBox}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search institutions..."
              className={styles.searchInput}
            />
            {searchTerm && (
              <button onClick={clearSearch} className={styles.clearSearchButton}>
                ✕
              </button>
            )}
          </div>
          
          <div className={styles.itemsPerPage}>
            <label>Show:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
              className={styles.itemsPerPageSelect}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span>per page</span>
          </div>
        </div>

        {/* Results Info */}
        <div className={styles.resultsInfo}>
          <span>
            Showing {startIndex + 1} to {Math.min(endIndex, sortedInstitutions.length)} of {sortedInstitutions.length} institutions
            {searchTerm && ` (filtered from ${institutions.length} total)`}
          </span>
        </div>

        {loading ? (
          <div className={styles.loading}>Loading institutions...</div>
        ) : institutions.length === 0 ? (
          <div className={styles.empty}>No institutions found</div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedInstitutions.length === currentInstitutions.length && currentInstitutions.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedInstitutions(currentInstitutions.map(inst => inst.id));
                        } else {
                          setSelectedInstitutions([]);
                        }
                      }}
                    />
                  </th>
                  <th 
                    className={styles.sortableHeader}
                    onClick={() => handleSort('name')}
                  >
                    Name
                    {sortField === 'name' && (
                      <span className={styles.sortIndicator}>
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className={styles.sortableHeader}
                    onClick={() => handleSort('short_name')}
                  >
                    Short Name
                    {sortField === 'short_name' && (
                      <span className={styles.sortIndicator}>
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className={styles.sortableHeader}
                    onClick={() => handleSort('code')}
                  >
                    Code
                    {sortField === 'code' && (
                      <span className={styles.sortIndicator}>
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className={styles.sortableHeader}
                    onClick={() => handleSort('category_name')}
                  >
                    Category
                    {sortField === 'category_name' && (
                      <span className={styles.sortIndicator}>
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className={styles.sortableHeader}
                    onClick={() => handleSort('created_at')}
                  >
                    Created
                    {sortField === 'created_at' && (
                      <span className={styles.sortIndicator}>
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentInstitutions.map((institution) => (
                  <tr key={institution.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedInstitutions.includes(institution.id)}
                        onChange={() => handleSelectInstitution(institution.id)}
                      />
                    </td>
                    <td>
                      {editingId === institution.id ? (
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className={styles.editInput}
                          onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit()}
                        />
                      ) : (
                        institution.name
                      )}
                    </td>
                    <td>
                      {editingId === institution.id ? (
                        <input
                          type="text"
                          value={editShortName}
                          onChange={(e) => setEditShortName(e.target.value)}
                          className={styles.editInput}
                          onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit()}
                        />
                      ) : (
                        institution.short_name || '-'
                      )}
                    </td>
                    <td>
                      {editingId === institution.id ? (
                        <input
                          type="text"
                          value={editCode}
                          onChange={(e) => setEditCode(e.target.value)}
                          className={styles.editInput}
                          onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit()}
                        />
                      ) : (
                        institution.code || '-'
                      )}
                    </td>
                    <td>
                      {editingId === institution.id ? (
                        <select
                          value={editCategory}
                          onChange={(e) => setEditCategory(parseInt(e.target.value))}
                          className={styles.editSelect}
                        >
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        institution.category_name || '-'
                      )}
                    </td>
                    <td>
                      {institution.created_at ? new Date(institution.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td>
                      {editingId === institution.id ? (
                        <div className={styles.editActions}>
                          <button onClick={handleSaveEdit} className={styles.saveButton}>
                            Save
                          </button>
                          <button onClick={handleCancelEdit} className={styles.cancelButton}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className={styles.actions}>
                          <button 
                            onClick={() => handleStartEdit(institution)} 
                            className={styles.editButton}
                          >
                            Edit
                          </button>
                          <button 
                            onClick={() => handleDeleteInstitution(institution.id)} 
                            className={styles.deleteButton}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <div className={styles.paginationInfo}>
              <span>
                Page {currentPage} of {totalPages}
              </span>
            </div>
            
            <div className={styles.paginationControls}>
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className={styles.paginationButton}
              >
                « First
              </button>
              
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={styles.paginationButton}
              >
                ‹ Previous
              </button>
              
              {/* Page Numbers */}
              <div className={styles.pageNumbers}>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`${styles.paginationButton} ${currentPage === pageNum ? styles.activePage : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={styles.paginationButton}
              >
                Next ›
              </button>
              
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className={styles.paginationButton}
              >
                Last »
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstitutionManager;
